import axios, { AxiosRequestConfig } from 'axios';
import { useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';
import { mutate } from 'swr';
import { useAPI, useFetch, useTranslate, useEnv } from 'hooks';
import { useModalContext } from 'contexts';
import { Logo, UploadProgress } from 'components/structure';
import { Photo, User, UserAPI } from 'interfaces/user';
import { ASSETS, USERS } from 'constants/endpoints';
import { LANGUAGE_KEY } from 'constants/enums';
import { revalidateOnChangeLanguage } from './events';

// Helper function to build S3 public URL
// Configure REACT_APP_CDN_URL or REACT_APP_S3_BUCKET_URL in your .env file
// Example: REACT_APP_CDN_URL=https://storage-files-tm1-eventos.s3.amazonaws.com
const buildS3PublicUrl = (fileName: string, cdnUrl?: string): string => {
  if (!fileName) return '';

  // If fileName is already a full URL, return it
  if (fileName.startsWith('http')) {
    return fileName;
  }

  // If CDN URL is provided, use it
  if (cdnUrl) {
    return `${cdnUrl}/${fileName}`;
  }

  // If no CDN URL is configured, return just the fileName
  // The backend should be configured to return full URLs
  console.warn(
    '⚠️ CDN URL não configurado. Adicione REACT_APP_CDN_URL no .env',
  );
  return fileName;
};

interface Params {
  name?: string;
  limit?: number;
  interests?: string[];
}

export const useUserById = (id: string) =>
  useFetch<{ user: User; accessToken: string }>(USERS.BY_ID(id));

export const useUserByUuid = (uuid: string) =>
  useFetch<{ user: User }>(USERS.BY_UUID(uuid));

export const useFetchOnlineUsers = (params: Params) => {
  const correctParams = params.name ? params : { ...params, limit: 10 };

  return useFetch<{ user: User[] }>(USERS.ONLINE, { params: correctParams });
};

export const useOnlineUsersByInterests = (interests: string[]) =>
  useFetch<{ user: User[] }>(USERS.ONLINE_BY_INTERESTS(interests));

export const useOnlineUsersByCountry = () =>
  useFetch(USERS.ONLINE_BY_COUNTRIES);

export const useCreateUser = () => {
  const api = useAPI();

  return async (user: User): Promise<UserAPI> => {
    const { data } = await api
      .post<UserAPI>(USERS.ROOT, user)
      .then((response) => response)
      .catch(({ response }) => response);
    if (data.user) {
      mutate(USERS.BY_ID(user.id!), data);
    }

    return data;
  };
};

export const useUpdateCloseEventUser = () => {
  const api = useAPI();

  const updateUserCloseEvent = useCallback(
    async (user: User) => {
      const { data } = await api
        .put<UserAPI>(USERS.BY_ID(user.id!), user)
        .then((response) => response)
        .catch(({ response }) => response);
      return data;
    },
    [api],
  );
  return { updateUserCloseEvent };
};

export const useUpdateUser = () => {
  const api = useAPI();
  const [isPending, setIsPending] = useState(false);
  const [, setCookie] = useCookies(['language']);

  const updateUser = useCallback(
    async (user: User) => {
      let cachedUser = {};
      setIsPending(true);

      mutate(
        USERS.BY_ID(user.id!),
        (currentUser: User) => {
          cachedUser = currentUser;

          return { ...currentUser, ...user };
        },
        false,
      );
      if (user.country) {
        localStorage.setItem('country', user.country!.toString());
      }
      if (user.preferableLanguage) {
        setCookie(LANGUAGE_KEY, user.preferableLanguage, {
          path: '/',
        });
        revalidateOnChangeLanguage(user.preferableLanguage);
      }

      try {
        const { data } = await api.patch<UserAPI>(USERS.ROOT, {
          ...user,
          completeRegistration: true,
        });
        mutate(USERS.BY_ID(user.id!), data);
        return data;
      } catch (error) {
        mutate(USERS.BY_ID(user.id!), cachedUser);
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    [api, setCookie],
  );

  return { isPending, updateUser };
};

export const useUploadPhoto = () => {
  const api = useAPI();
  const translate = useTranslate();
  const { cdnUrl } = useEnv();
  const [isPending, setIsPending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { setModalProps, setShowModal } = useModalContext();

  const onInit = () => {
    setModalProps({
      title: translate('users.waitAMoment'),
      icon: <Logo medium />,
      noBackground: true,
      hasButton: false,
      content: <UploadProgress value={uploadProgress} />,
    });
    setShowModal(true);
  };

  const uploadPhoto = async (
    { file, fileContext, originalFile }: Photo,
    userId?: string,
  ) => {
    const options = {
      headers: { 'Content-Type': '' },
      onUploadProgress({ loaded, total }) {
        setUploadProgress(Math.round((loaded * 100) / total));
      },
    } as AxiosRequestConfig;

    try {
      onInit();
      setIsPending(true);

      // console.log('🔵 ========== INICIANDO UPLOAD DA FOTO ==========');
      // console.log('🔵 Dados do arquivo:', {
      //   fileName: file.name,
      //   fileSize: file.size,
      //   fileType: file.type,
      //   fileContext,
      //   userId,
      //   cdnUrl,
      // });
      // console.log(
      //   '🔵 Original file:',
      //   originalFile
      //     ? {
      //         name: originalFile.name,
      //         size: originalFile.size,
      //       }
      //     : 'Não fornecido',
      // );

      // Upload avatar - Get pre-signed URL
      // console.log('🔵 Solicitando URL pré-assinada para avatar...');
      const preSignedResponse = await api.get(ASSETS.PRE_SIGNED, {
        params: { fileName: file.name, fileContext },
      });

      // console.log(
      //   '🔵 Resposta completa do PRE_SIGNED:',
      //   JSON.stringify(preSignedResponse.data, null, 2),
      // );

      const { url } = preSignedResponse.data;
      // console.log('🔵 URL pré-assinada obtida:', url);

      // console.log('🔵 Fazendo upload do arquivo para S3...');
      await axios.put(url, file, options);
      // console.log('✅ Upload do avatar para S3 concluído com sucesso!');

      // Build public URL for the uploaded avatar
      // const avatarPublicUrl = buildS3PublicUrl(file.name, cdnUrl);
      const avatarPublicUrl = url?.split('?')[0];

      // console.log('🔵 ========== CONSTRUINDO URL PÚBLICA ==========');
      // console.log('🔵 Nome do arquivo:', file.name);
      // console.log('🔵 CDN URL configurado:', cdnUrl || 'NÃO CONFIGURADO');
      // console.log('🔵 URL pública construída:', avatarPublicUrl);
      // console.log('🔵 URL é completa?', avatarPublicUrl.startsWith('http'));

      let originalPublicUrl = '';
      // Upload original photo if provided
      if (originalFile) {
        // console.log('🔵 ========== UPLOAD DA FOTO ORIGINAL ==========');
        // console.log('🔵 Solicitando URL pré-assinada para original...');
        const originalPreSignedResponse = await api.get(ASSETS.PRE_SIGNED, {
          params: { fileName: originalFile.name, fileContext: 'original' },
        });

        // console.log(
        //   '🔵 Resposta do PRE_SIGNED (original):',
        //   JSON.stringify(originalPreSignedResponse.data, null, 2),
        // );

        const { url: originalSignedUrl } = originalPreSignedResponse.data;

        // console.log('🔵 Fazendo upload da foto original para S3...');
        await axios.put(originalSignedUrl, originalFile, options);
        originalPublicUrl = buildS3PublicUrl(originalFile.name, cdnUrl);

        // console.log('✅ Upload da foto original concluído');
        // console.log('🔵 URL pública da foto original:', originalPublicUrl);
      }

      // Update user with photo URLs
      if (userId) {
        const updateData: Partial<User> = {
          id: userId,
          avatar: avatarPublicUrl,
        };

        if (originalPublicUrl) {
          updateData.original = avatarPublicUrl;
        }

        // console.log('🔵 ========== ATUALIZANDO USUÁRIO ==========');
        // console.log('🔵 User ID:', userId);
        // console.log('🔵 Endpoint:', USERS.ROOT);
        // console.log(
        //   '🔵 Dados a serem enviados:',
        //   JSON.stringify(updateData, null, 2),
        // );

        const updateResponse = await api.patch<UserAPI>(USERS.ROOT, updateData);

        // console.log(
        //   '✅ Resposta da API (atualização do usuário):',
        //   JSON.stringify(updateResponse.data, null, 2),
        // );
        // console.log(
        //   '✅ Avatar salvo no banco:',
        //   updateResponse.data.user?.avatar,
        // );
        // console.log(
        //   '✅ Original salvo no banco:',
        //   updateResponse.data.user?.original,
        // );

        // console.log('🔵 Revalidando cache do SWR para:', USERS.BY_ID(userId));
        mutate(USERS.BY_ID(userId), updateResponse.data, false);
        // console.log('✅ Cache revalidado');
      } else {
        // console.warn(
        //   '⚠️ User ID não fornecido, usuário não será atualizado no banco!',
        // );
      }

      // console.log('🔵 ========== UPLOAD CONCLUÍDO COM SUCESSO ==========');
      const result = {
        success: true,
        avatar: avatarPublicUrl,
        original: avatarPublicUrl,
      };
      // console.log('🔵 Resultado final:', result);
      return result;
    } catch (error: any) {
      // console.error('❌ ========== ERRO NO UPLOAD DA FOTO ==========');
      // console.error('❌ Tipo de erro:', error?.constructor?.name);
      // console.error('❌ Mensagem:', error?.message);
      // console.error('❌ Response status:', error?.response?.status);
      // console.error('❌ Response data:', error?.response?.data);
      // console.error('❌ Stack trace:', error?.stack);
      // console.error('❌ Erro completo:', error);
      return { success: false, error };
    } finally {
      // console.log('🔵 Fechando modal e finalizando upload...');
      setShowModal(false);
      setIsPending(false);
    }
  };

  return { isPending, uploadPhoto };
};
